import {
  SLIDE_DURATION_MAX_MS,
  SLIDE_DURATION_MIN_MS,
  SLIDE_FETCH_TIMEOUT_MS,
  SLIDE_INTERVAL_MS,
  SLIDE_REFRESH_MS,
  SLIDES_URL,
} from '../config/kiosk'

/**
 * The list of slides the panel shows, kept up to date from the church website.
 *
 * - On boot the last list that worked is read from localStorage, so the panel shows
 *   pictures at once and keeps showing them with no network.
 * - Every few minutes the website is asked again. Only an answer that is clearly a
 *   list replaces what is shown; a failure of any kind keeps the current slides.
 * - Pictures new to this panel are downloaded one at a time before the list is
 *   handed over, so a slide never appears half-loaded. The service worker keeps
 *   what is downloaded (vite.config.ts), which is what makes the panel work offline.
 */

export interface Slide {
  id: number
  url: string
  durationMs: number
}

export interface SlideStatus {
  lastSuccessAt: string | null
  lastFailureAt: string | null
  lastFailure: string | null
  generatedAt: string | null
  count: number
}

const STORAGE_KEY = 'kiosk.slides.v1'

interface Saved {
  slides: Slide[]
  generatedAt: string | null
  savedAt: string
}

export class SlideStore {
  readonly status: SlideStatus = {
    lastSuccessAt: null,
    lastFailureAt: null,
    lastFailure: null,
    generatedAt: null,
    count: 0,
  }

  private readonly fetched = new Set<string>()
  private refreshing = false

  /**
   * @param getDisplayed the slides actually on screen (without any that failed to
   *   load), so a picture that failed is tried again on the next refresh.
   * @param onList receives each new list; the slideshow decides when to show it.
   */
  constructor(
    private readonly panel: string | undefined,
    private readonly getDisplayed: () => Slide[],
    private readonly onList: (slides: Slide[]) => void,
  ) {}

  /** The list saved by the last successful refresh, or null. */
  loadSaved(): Slide[] | null {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      const saved = JSON.parse(raw) as Partial<Saved>
      const slides = parseSlides(saved.slides)
      if (!slides) return null
      this.status.generatedAt = typeof saved.generatedAt === 'string' ? saved.generatedAt : null
      this.status.count = slides.length
      return slides
    } catch {
      return null
    }
  }

  start(): void {
    void this.refresh()
    window.setInterval(() => void this.refresh(), SLIDE_REFRESH_MS)
  }

  async refresh(): Promise<void> {
    if (this.refreshing) return
    this.refreshing = true
    try {
      const { slides, generatedAt } = await this.fetchList()
      const showing = this.getDisplayed().map((slide) => slide.url)
      const ready: Slide[] = []
      const failures: string[] = []
      for (const slide of slides) {
        const problem = await this.prefetch(slide.url)
        if (problem) failures.push(`#${slide.id} ${problem}`)
        // A picture that is on screen already stays even if this download failed:
        // a hiccup on the media host must not take slides down.
        if (!problem || showing.indexOf(slide.url) !== -1) ready.push(slide)
      }

      this.status.lastSuccessAt = new Date().toISOString()
      this.status.generatedAt = generatedAt
      if (failures.length) this.fail(`이미지 ${failures.join(', ')}`)

      if (sameSlides(ready, this.getDisplayed())) return
      this.status.count = ready.length
      this.save({ slides: ready, generatedAt, savedAt: new Date().toISOString() })
      this.onList(ready)
    } catch (error) {
      this.fail(error instanceof Error ? error.message : String(error))
    } finally {
      this.refreshing = false
    }
  }

  private async fetchList(): Promise<{ slides: Slide[]; generatedAt: string | null }> {
    const url = this.panel ? `${SLIDES_URL}?panel=${encodeURIComponent(this.panel)}` : SLIDES_URL
    const response = await withTimeout(fetch(url, { credentials: 'omit' }), SLIDE_FETCH_TIMEOUT_MS)
    const type = response.headers.get('content-type') || ''
    if (!response.ok) throw new Error(`목록 요청 실패 (${response.status})`)
    if (type.indexOf('application/json') === -1) throw new Error(`목록 응답 형식 오류 (${type})`)
    const body = (await response.json()) as { slides?: unknown; generatedAt?: unknown }
    const slides = parseSlides(body.slides)
    if (!slides) throw new Error('목록 응답에 slides 배열이 없음')
    return { slides, generatedAt: typeof body.generatedAt === 'string' ? body.generatedAt : null }
  }

  /** Downloads a picture once per boot so the service worker has it. Returns a problem, or null. */
  private async prefetch(url: string): Promise<string | null> {
    if (this.fetched.has(url)) return null
    try {
      const response = await withTimeout(fetch(url, { mode: 'cors', credentials: 'omit' }), SLIDE_FETCH_TIMEOUT_MS)
      if (!response.ok) return `HTTP ${response.status}`
      // Read the body so the download completes (and is cached) without decoding it.
      await response.arrayBuffer()
      this.fetched.add(url)
      return null
    } catch (error) {
      return error instanceof Error ? error.message : String(error)
    }
  }

  private fail(reason: string): void {
    this.status.lastFailureAt = new Date().toISOString()
    this.status.lastFailure = reason
    console.warn('[slides]', reason)
  }

  private save(saved: Saved): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(saved))
    } catch {
      // Storage full or blocked: the panel still works, it just starts empty offline.
    }
  }
}

/**
 * Keeps the slides this build understands. Unknown kinds are skipped, so the website
 * can add video or other kinds without breaking panels that have not updated yet.
 */
export function parseSlides(value: unknown): Slide[] | null {
  if (!Array.isArray(value)) return null
  const slides: Slide[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const { id, kind, url, durationMs } = item as Record<string, unknown>
    if (kind !== undefined && kind !== 'image') continue
    if (typeof id !== 'number' || typeof url !== 'string' || !url) continue
    slides.push({ id, url, durationMs: clampDuration(durationMs) })
  }
  return slides
}

function clampDuration(value: unknown): number {
  if (typeof value !== 'number' || !isFinite(value)) return SLIDE_INTERVAL_MS
  return Math.min(SLIDE_DURATION_MAX_MS, Math.max(SLIDE_DURATION_MIN_MS, value))
}

export function sameSlides(a: Slide[], b: Slide[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i]
    const y = b[i]
    if (!x || !y || x.id !== y.id || x.url !== y.url || x.durationMs !== y.durationMs) return false
  }
  return true
}

/** No AbortController on the oldest WebViews this targets, so the request is just abandoned. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`시간 초과 (${ms / 1000}초)`)), ms)
    promise.then(
      (value) => {
        window.clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        window.clearTimeout(timer)
        reject(error)
      },
    )
  })
}
